import { useState, useEffect, useRef } from 'react';
import {
  Container,
  Stack,
  Paper,
  Group,
  Button,
  Badge,
  Text,
  ActionIcon,
  Modal,
  Select,
  TextInput,
  Switch,
  NumberInput,
  Loader,
  Center,
  Alert,
  Card,
  Title,
} from '@mantine/core';
import { IconPlus, IconTrash, IconCheck, IconX, IconAlertCircle, IconSparkles } from '@tabler/icons-react';
import {
  useProviders,
  useCreateProvider,
  useUpdateProvider,
  useDeleteProvider,
  useTestProvider,
  useSetPrimaryProvider,
  LLMProvider,
  CreateProviderRequest,
} from '../api/llm';
import { PromptTemplateManager } from '../components/settings/PromptTemplateManager';
import { PageHeader } from '../components/common/PageHeader';
import { StylesheetManager } from '../components/settings/StylesheetManager';
import { LLMRequestsPanel } from '../components/settings/LLMRequestsPanel';
import { ScrapingRulesPanel } from '../components/settings/ScrapingRulesPanel';
import { getAuthHeaders } from '../api/client';
import { notifications } from '@mantine/notifications';

const PROVIDER_MODELS: Record<string, string[]> = {
  anthropic: [
    'claude-sonnet-4-5-20250929',
    'claude-opus-4-5-20251101',
    'claude-3-5-haiku-20241022',
    'claude-haiku-4-5-20251001',
  ],
  openai: ['gpt-4-turbo', 'gpt-4o', 'gpt-4o-mini'],
  gemini: ['gemini-2.0-pro', 'gemini-1.5-pro', 'gemini-1.5-flash'],
};



export default function SettingsPage() {
  const { data: providers = [], isLoading: providersLoading } = useProviders();
  const createMutation = useCreateProvider();

  // Delete Data State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeletingData, setIsDeletingData] = useState(false);

  const handleDeleteAllData = async () => {
    if (!deletePassword) return;

    setIsDeletingData(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/auth/delete-data', {
        method: 'POST',
        headers,
        body: JSON.stringify({ password: deletePassword }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete data');
      }

      notifications.show({
        title: 'Data Deleted',
        message: 'All your data has been permanently deleted.',
        color: 'green',
      });

      setDeleteModalOpen(false);
      setDeletePassword('');
      // Ideally force a reload or refresh of all queries
      window.location.reload();
    } catch (error) {
      notifications.show({
        title: 'Deletion Failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        color: 'red',
      });
    } finally {
      setIsDeletingData(false);
    }
  };

  // Use a ref to track mutations being executed
  const pendingMutationRef = useRef<{
    type: 'test' | 'delete' | 'setPrimary' | 'update';
    id: string;
  } | null>(null);

  // State for tracking which provider is being edited/deleted/tested
  const [currentProviderId, setCurrentProviderId] = useState<string | null>(null);

  // Mutations - call at top level with current provider ID
  const updateMutation = useUpdateProvider(currentProviderId || '');
  const deleteMutation = useDeleteProvider(currentProviderId || '');
  const testMutation = useTestProvider(currentProviderId || '');
  const setPrimaryMutation = useSetPrimaryProvider(currentProviderId || '');

  // Execute pending mutations when currentProviderId is set
  useEffect(() => {
    if (!pendingMutationRef.current || !currentProviderId) return;

    const { type, id } = pendingMutationRef.current;
    if (id !== currentProviderId) return; // Still waiting for state update

    const executeMutation = async () => {
      try {
        switch (type) {
          case 'test': {
            const result = await testMutation.mutateAsync();
            setTestResult({
              success: true,
              message: `Connected successfully to ${result.display_name}`,
            });
            break;
          }
          case 'delete': {
            await deleteMutation.mutateAsync();
            setTestResult({
              success: true,
              message: 'Provider deleted successfully',
            });
            break;
          }
          case 'setPrimary': {
            await setPrimaryMutation.mutateAsync();
            setTestResult({
              success: true,
              message: 'Provider set as primary',
            });
            break;
          }
        }
      } catch (error) {
        setTestResult({
          success: false,
          message: error instanceof Error ? error.message : `${type} operation failed`,
        });
      } finally {
        setTestingId(null);
        pendingMutationRef.current = null;
        setCurrentProviderId(null);
      }
    };

    executeMutation();
  }, [currentProviderId, testMutation, deleteMutation, setPrimaryMutation]);

  // Modal state
  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<
    Omit<CreateProviderRequest, 'api_key'> & { api_key?: string }
  >({
    name: 'anthropic',
    display_name: '',
    api_key: '',
    model_id: 'claude-sonnet-4-5-20250929',
    config: {
      temperature: 0.7,
      max_tokens: 4096,
    },
  });

  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleOpenModal = (provider?: LLMProvider) => {
    if (provider) {
      setEditingId(provider.id);
      setFormData({
        name: provider.name,
        display_name: provider.display_name,
        api_key: '', // Don't expose API key
        model_id: provider.model_id,
        config: provider.config,
      });
    } else {
      setEditingId(null);
      setFormData({
        name: 'anthropic',
        display_name: '',
        api_key: '',
        model_id: 'claude-sonnet-4-5-20250929',
        config: {
          temperature: 0.7,
          max_tokens: 4096,
        },
      });
    }
    setTestResult(null);
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setEditingId(null);
    setTestResult(null);
  };

  const handleSave = async () => {
    if (!formData.display_name || !formData.api_key || !formData.model_id) {
      setTestResult({
        success: false,
        message: 'All fields are required',
      });
      return;
    }

    try {
      if (editingId) {
        // Update existing provider
        console.log('[SettingsPage] Updating provider:', editingId);
        setCurrentProviderId(editingId);
        await updateMutation.mutateAsync({
          display_name: formData.display_name,
          api_key: formData.api_key,
          model_id: formData.model_id,
          config: formData.config,
        });
      } else {
        // Create new provider
        console.log('[SettingsPage] Creating new provider:', formData);
        const result = await createMutation.mutateAsync({
          name: formData.name as 'anthropic' | 'openai' | 'gemini',
          display_name: formData.display_name,
          api_key: formData.api_key,
          model_id: formData.model_id,
          config: formData.config,
        });
        console.log('[SettingsPage] Create result:', result);
      }
      handleCloseModal();
      setCurrentProviderId(null);
    } catch (error) {
      console.error('[SettingsPage] Save error:', error);
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : 'Failed to save provider',
      });
    }
  };

  const handleTestConnection = (providerId?: string) => {
    // For new providers, we can't test without creating them first
    if (!providerId) {
      setTestResult({
        success: false,
        message: 'Please save the provider first before testing',
      });
      return;
    }

    setTestingId(providerId);
    pendingMutationRef.current = { type: 'test', id: providerId };
    setCurrentProviderId(providerId);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this provider?')) {
      pendingMutationRef.current = { type: 'delete', id };
      setCurrentProviderId(id);
    }
  };

  const handleSetPrimary = (id: string) => {
    pendingMutationRef.current = { type: 'setPrimary', id };
    setCurrentProviderId(id);
  };

  const primaryProvider = providers.find((p) => p.is_primary);
  const availableProviders = providers.filter((p) => p.is_active);

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <PageHeader
          title="Settings"
          subtitle="Configure your LLM providers for AI-powered resume generation"
        />

        {/* LLM Providers Section */}
        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <div>
              <Group justify="space-between" align="center">
                <div>
                  <Title order={2} size="h3">
                    LLM Providers
                  </Title>
                  <Text size="sm" c="dimmed">
                    Manage your AI provider configurations for intelligent resume generation
                  </Text>
                </div>
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={() => handleOpenModal()}
                >
                  Add Provider
                </Button>
              </Group>
            </div>

            {/* Providers List */}
            {providersLoading ? (
              <Center py="xl">
                <Loader />
              </Center>
            ) : availableProviders.length === 0 ? (
              <Alert icon={<IconAlertCircle size={16} />} color="blue">
                No LLM providers configured. Add a provider to enable AI resume generation.
              </Alert>
            ) : (
              <Stack gap="sm">
                {availableProviders.map((provider) => (
                  <Card key={provider.id} padding="sm" radius="md" withBorder>
                    <Card.Section p="sm" inheritPadding>
                      <Group justify="space-between" align="center">
                        <div style={{ flex: 1 }}>
                          <Group gap="xs">
                            <div>
                              <Text fw={500} size="sm">
                                {provider.display_name}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {provider.model_id}
                              </Text>
                            </div>
                            {provider.is_primary && <Badge color="blue">Primary</Badge>}
                            {!provider.is_active && <Badge color="gray">Inactive</Badge>}
                          </Group>
                        </div>

                        <Group gap="xs">
                          {!provider.is_primary && (
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => handleSetPrimary(provider.id)}
                            >
                              Make Primary
                            </Button>
                          )}

                          <Button
                            size="xs"
                            variant="default"
                            onClick={() => handleTestConnection(provider.id)}
                            loading={testingId === provider.id}
                          >
                            Test
                          </Button>

                          <Button
                            size="xs"
                            variant="default"
                            onClick={() => handleOpenModal(provider)}
                          >
                            Edit
                          </Button>

                          <ActionIcon
                            color="red"
                            size="sm"
                            variant="light"
                            onClick={() => handleDelete(provider.id)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Group>
                    </Card.Section>
                  </Card>
                ))}
              </Stack>
            )}

            {primaryProvider && (
              <Alert icon={<IconSparkles size={16} />} color="green">
                Using <strong>{primaryProvider.display_name}</strong> for AI generation
              </Alert>
            )}
          </Stack>
        </Paper>

        {/* LLM Request Logs Section */}
        <LLMRequestsPanel />

        {/* Prompt Templates Section */}
        <PromptTemplateManager />

        {/* PDF Stylesheets Section */}
        <StylesheetManager />

        {/* Scraping Rules Section */}
        <ScrapingRulesPanel />

        {/* Provider Configuration Modal */}
        <Modal
          opened={isOpen}
          onClose={handleCloseModal}
          title={editingId ? 'Edit Provider' : 'Add New Provider'}
          size="md"
        >
          <Stack gap="md">
            {testResult && (
              <Alert
                icon={testResult.success ? <IconCheck size={16} /> : <IconX size={16} />}
                color={testResult.success ? 'green' : 'red'}
              >
                {testResult.message}
              </Alert>
            )}

            {/* Provider Type */}
            {!editingId && (
              <Select
                label="Provider Type"
                placeholder="Select a provider"
                data={[
                  { value: 'anthropic', label: 'Claude (Anthropic)' },
                  { value: 'openai', label: 'GPT (OpenAI)' },
                  { value: 'gemini', label: 'Gemini (Google)' },
                ]}
                value={formData.name}
                onChange={(value) => {
                  if (value) {
                    setFormData({
                      ...formData,
                      name: value as 'anthropic' | 'openai' | 'gemini',
                      model_id: PROVIDER_MODELS[value]?.[0] || '',
                    });
                  }
                }}
              />
            )}

            {editingId && (
              <Text size="sm" c="dimmed">
                Provider Type: <strong>{formData.name}</strong>
              </Text>
            )}

            {/* Display Name */}
            <TextInput
              label="Display Name"
              placeholder="e.g., Claude (Default)"
              value={formData.display_name}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  display_name: e.currentTarget.value,
                })
              }
            />

            {/* API Key */}
            <TextInput
              label="API Key"
              type="password"
              placeholder="sk-..."
              description={editingId ? 'Leave blank to keep existing key, or enter a new one' : 'Your API key will be securely stored'}
              value={formData.api_key || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  api_key: e.currentTarget.value,
                })
              }
            />

            {/* Model ID */}
            <Select
              label="Model ID"
              placeholder="Select a model"
              data={PROVIDER_MODELS[formData.name] || []}
              value={formData.model_id}
              onChange={(value) => {
                if (value) {
                  setFormData({
                    ...formData,
                    model_id: value,
                  });
                }
              }}
            />

            {/* Temperature */}
            <NumberInput
              label="Temperature"
              description="Controls randomness. Lower = more focused (0.0-1.0)"
              min={0}
              max={1}
              step={0.1}
              value={formData.config?.temperature || 0.7}
              onChange={(value) => {
                if (typeof value === 'number') {
                  setFormData({
                    ...formData,
                    config: {
                      ...formData.config,
                      temperature: value,
                    },
                  });
                }
              }}
            />

            {/* Max Tokens */}
            <NumberInput
              label="Max Tokens"
              description="Maximum response length"
              min={256}
              max={32000}
              step={256}
              value={formData.config?.max_tokens || 4096}
              onChange={(value) => {
                if (typeof value === 'number') {
                  setFormData({
                    ...formData,
                    config: {
                      ...formData.config,
                      max_tokens: value,
                    },
                  });
                }
              }}
            />

            {/* Action Buttons */}
            <Group justify="space-between">
              <Group gap="xs">
                <Button
                  variant="default"
                  onClick={() => handleTestConnection(editingId || undefined)}
                  loading={testingId === (editingId || 'new')}
                  disabled={!editingId}
                  title={!editingId ? 'Save provider first before testing' : undefined}
                >
                  Test Connection
                </Button>
              </Group>

              <Group gap="xs">
                <Button variant="default" onClick={handleCloseModal}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  loading={createMutation.isPending}
                >
                  {editingId ? 'Save Changes' : 'Add Provider'}
                </Button>
              </Group>
            </Group>
          </Stack>
        </Modal>

        {/* Information Card */}
        <Paper
          p="lg"
          radius="md"
          style={{
            backgroundColor: 'var(--mantine-color-gray-8)',
            borderLeft: '4px solid var(--mantine-color-blue-6)',
          }}
        >
          <Stack gap="sm">
            <Title order={3} size="h4">
              About LLM Integration
            </Title>
            <Text size="sm">
              LLM integration allows you to automatically generate tailored resumes for specific
              jobs. Each provider has its own API key and configuration.
            </Text>
            <Text size="sm" fw={500}>
              Getting Started:
            </Text>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '0.875rem', color: 'var(--mantine-color-gray-1)' }}>
              <li>Get an API key from your chosen provider (Anthropic, OpenAI, or Google)</li>
              <li>Add the provider configuration in this settings page</li>
              <li>Test the connection to verify your API key works</li>
              <li>Use &quot;Generate with AI&quot; button in job detail view to create resumes</li>
            </ul>
          </Stack>
        </Paper>
        <Paper p="lg" radius="md" withBorder style={{ borderColor: 'var(--mantine-color-red-8)' }}>
          <Stack gap="md">
            <div>
              <Title order={2} size="h3" c="red">
                Danger Zone
              </Title>
              <Text c="dimmed" size="sm">
                Irreversible actions that affect your account data
              </Text>
            </div>

            <Group justify="space-between" align="center">
              <div>
                <Text fw={500}>Delete All Data</Text>
                <Text size="sm" c="dimmed">
                  Permanently delete all your jobs, resumes, notes, and settings. Your account will remain.
                </Text>
              </div>
              <Button
                color="red"
                variant="light"
                onClick={() => setDeleteModalOpen(true)}
              >
                Delete Data
              </Button>
            </Group>
          </Stack>
        </Paper>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setDeletePassword('');
          }}
          title={<Text c="red" fw={700}>Delete All Data?</Text>}
        >
          <Stack>
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              This action cannot be undone. All your created content will be permanently lost.
            </Alert>

            <Text size="sm">
              Please enter your login password to confirm deletion:
            </Text>

            <TextInput
              type="password"
              placeholder="Your password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.currentTarget.value)}
            />

            <Group justify="flex-end" mt="md">
              <Button
                variant="default"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setDeletePassword('');
                }}
              >
                Cancel
              </Button>
              <Button
                color="red"
                loading={isDeletingData}
                disabled={!deletePassword}
                onClick={handleDeleteAllData}
              >
                Permanently Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </Container >
  );
}
